import { FolderActions, Folder, WorkspaceState } from './types';

/**
 * 创建文件夹相关操作
 * 处理文件夹的创建、重命名、删除、移动等操作
 * 
 * @param set 状态更新函数
 * @returns 文件夹操作方法集合
 */
export const createFolderActions = (
  set: (state: WorkspaceState | ((state: WorkspaceState) => WorkspaceState)) => void
): FolderActions => {
  return {
    /**
     * 创建文件夹
     * @param name 文件夹名称
     * @param parentId 父文件夹ID，默认为root
     * @returns 创建的文件夹ID
     */
    createFolder: (name, parentId = 'root') => {
      const id = `folder-${Date.now()}`
      const newFolder: Folder = {
        id,
        name,
        documents: [],
        createdAt: Date.now(),
        parentId,
        children: []
      }
      
      set((state: WorkspaceState) => {
        const newFolders = { ...state.folders };
        
        // 添加新文件夹
        newFolders[id] = newFolder;
        
        // 更新父文件夹的子项列表
        if (parentId && newFolders[parentId]) {
          newFolders[parentId] = {
            ...newFolders[parentId],
            children: [...newFolders[parentId].children, id]
          };
        }
        
        return {
          ...state,
          folders: newFolders,
          activeFolderId: id
        };
      });
      
      return id;
    },
    
    /**
     * 重命名文件夹
     * @param id 文件夹ID
     * @param name 新名称
     */
    renameFolder: (id, name) => {
      set((state: WorkspaceState) => {
        if (!state.folders[id]) {
          return state;
        }
        
        return {
          ...state,
          folders: {
            ...state.folders,
            [id]: {
              ...state.folders[id],
              name
            }
          }
        };
      });
    },
    
    /**
     * 删除文件夹
     * @param id 文件夹ID
     */
    deleteFolder: (id) => {
      set((state: WorkspaceState) => {
        // 不允许删除根文件夹
        if (id === 'root' || !state.folders[id]) {
          return state;
        }
        
        // 创建新的文件夹集合，准备移除目标文件夹
        const newFolders = { ...state.folders };
        
        // 获取要删除的文件夹
        const deletedFolder = newFolders[id];
        
        // 如果文件夹有父级，从父级的children列表中移除
        if (deletedFolder.parentId && newFolders[deletedFolder.parentId]) {
          newFolders[deletedFolder.parentId] = {
            ...newFolders[deletedFolder.parentId],
            children: newFolders[deletedFolder.parentId].children.filter((childId: string) => childId !== id)
          };
        }
        
        // 递归删除此文件夹的所有子文件夹
        const foldersToDelete = [id];
        const collectSubfolders = (folderId: string) => {
          const folder = newFolders[folderId];
          if (folder) {
            folder.children.forEach((childId: string) => {
              foldersToDelete.push(childId);
              collectSubfolders(childId);
            });
          }
        };
        
        collectSubfolders(id);
        
        // 从folders对象中移除所有要删除的文件夹
        for (const folderId of foldersToDelete) {
          delete newFolders[folderId];
        }
        
        // 如果当前活动文件夹是被删除的文件夹，则将活动文件夹设置为root
        const newActiveFolderId = state.activeFolderId === id ? 'root' : state.activeFolderId;
        
        return {
          ...state,
          folders: newFolders,
          activeFolderId: newActiveFolderId
        };
      });
    },
    
    /**
     * 将文档移动到文件夹
     * @param documentId 文档ID
     * @param folderId 目标文件夹ID
     */
    moveDocumentToFolder: (documentId, folderId) => {
      set((state: WorkspaceState) => {
        // 检查文档和文件夹是否存在
        if (!state.documents[documentId] || !state.folders[folderId]) {
          return state;
        }
        
        // 查找文档当前所在的文件夹
        let currentFolderId: string | null = null;
        for (const [id, folder] of Object.entries(state.folders)) {
          if (folder.documents.includes(documentId)) {
            currentFolderId = id;
            break;
          }
        }
        
        // 如果文档已经在目标文件夹中，不做任何改变
        if (currentFolderId === folderId) {
          return state;
        }
        
        // 创建新的文件夹集合
        const newFolders = { ...state.folders };
        
        // 从当前文件夹中移除文档
        if (currentFolderId) {
          const folder = newFolders[currentFolderId];
          newFolders[currentFolderId] = {
            ...folder,
            documents: folder.documents.filter(
              (docId: string) => docId !== documentId
            )
          };
        }
        
        // 将文档添加到目标文件夹
        newFolders[folderId] = {
          ...newFolders[folderId],
          documents: [...newFolders[folderId].documents, documentId]
        };
        
        return {
          ...state,
          folders: newFolders
        };
      });
    },
    
    /**
     * 将文件夹移动到另一个文件夹
     * @param folderId 要移动的文件夹ID
     * @param parentId 目标父文件夹ID
     */
    moveFolderToFolder: (folderId, parentId) => {
      set((state: WorkspaceState) => {
        // 边界条件检查
        if (
          folderId === 'root' || // 不能移动根文件夹
          !state.folders[folderId] || // 文件夹必须存在
          !state.folders[parentId] || // 目标父文件夹必须存在
          folderId === parentId || // 不能将文件夹移动到自身
          state.folders[folderId].parentId === parentId // 已经在目标文件夹中
        ) {
          return state;
        }
        
        // 检查目标文件夹是否是要移动文件夹的子文件夹（避免循环引用）
        let current: string | null = parentId;
        while (current !== 'root' && current !== null) {
          if (current === folderId) {
            // 找到循环引用，不执行移动
            return state;
          }
          if (!state.folders[current]) {
            break;
          }
          current = state.folders[current].parentId;
        }
        
        // 创建新的文件夹集合
        const newFolders = { ...state.folders };
        
        // 从原父文件夹的children中移除
        const oldParentId = newFolders[folderId].parentId;
        if (oldParentId && newFolders[oldParentId]) {
          newFolders[oldParentId] = {
            ...newFolders[oldParentId],
            children: newFolders[oldParentId].children.filter(id => id !== folderId)
          };
        }
        
        // 添加到新父文件夹的children中
        newFolders[parentId] = {
          ...newFolders[parentId],
          children: [...newFolders[parentId].children, folderId]
        };
        
        // 更新文件夹的parentId
        newFolders[folderId] = {
          ...newFolders[folderId],
          parentId
        };
        
        return {
          ...state,
          folders: newFolders
        };
      });
    },
    
    /**
     * 设置活动文件夹
     * @param id 文件夹ID
     */
    setActiveFolder: (id) => {
      set((state: WorkspaceState) => {
        if (!id || !state.folders[id]) {
          return state;
        }
        
        return {
          ...state,
          activeFolderId: id
        };
      });
    }
  };
}; 